/** Plaintext same-tab manual authoring continuation under composerDrafts policy.
 * Persist exact commands (including prose and review tickets) before dispatch.
 * Loading never executes or infers acceptance from current server content.
 */
import { z } from 'zod';
import { feedbackContinuationPrefix, isCurrentDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import { captureFeedbackWorkspaceCommand } from './feedbackWorkspaces';
import { captureFeedbackDraftCommand } from './feedbackDrafts';
import { captureFeedbackPublication } from './feedbackPublication';
import type { FeedbackWorkspaceCommand, FeedbackDraftCommand, FeedbackPublicationIntent,
  FeedbackReviewIntent, FeedbackPublicationResult } from './types';

const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
const counter = z.number().int().min(1).max(2147483647);
const zeroCounter = z.number().int().min(0).max(2147483647);
// Pydantic text limits count Unicode codepoints, not UTF-16 code units.
const boundedText = (max: number, min = 0) => z.string().refine(value => {
  const length = [...value].length; return length >= min && length <= max;
});
// FeedbackContent / workspace prose validate length after Python str.strip().
// This differs from JS trim (NEL/control separators strip; U+FEFF does not).
// Validate the normalized length only: persisted/replayed bytes stay untouched.
const outerServerWhitespace = /^[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+|[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+$/g;
export const feedbackProseLimits = Object.freeze({ message:4000, next_step:2000 } as const);
/** Inspect server-normalized codepoint length without changing authored text.
 * Forms and prose-only workspace repair use this same rule before capture. */
export function feedbackProseLength(value: string): number {
  return [...value.replace(outerServerWhitespace, '')].length;
}
export function isFeedbackProseWithinLimit(field: keyof typeof feedbackProseLimits, value: string): boolean {
  return feedbackProseLength(value) <= feedbackProseLimits[field];
}
const prose = (field: keyof typeof feedbackProseLimits) => z.string().refine(value => isFeedbackProseWithinLimit(field,value));
const textId = boundedText(200, 1);
const decimal = z.string().refine(value => /^[1-9][0-9]{0,18}$/.test(value) && BigInt(value) <= 9223372036854775807n);
const goalId = z.union([z.number().int().min(1).max(Number.MAX_SAFE_INTEGER), decimal]);
const content = z.object({
  message: prose('message').optional(), next_step: prose('next_step').optional(),
  subject_mode: z.enum(['players','team']).nullable().optional(),
  player_ids: z.array(textId).max(50).optional(),
  audience: z.enum(['coaches','players','team']).nullable().optional(),
  associated_match_id: boundedText(120, 1).nullable().optional(),
  resource_links: z.array(z.object({ resource_id: uuid, revision_id: uuid }).strict()).max(10).optional(),
  goal_ids: z.array(goalId).max(10).optional(),
  ref_video_provider: z.enum(['youtube','vimeo','file']).nullable().optional(),
  ref_video_id: boundedText(2048).nullable().optional(),
  ref_video_label: boundedText(200).nullable().optional(),
  ref_video_title: boundedText(300).nullable().optional(),
  ref_video_thumb: boundedText(500).nullable().optional(),
}).strict();
const workspace = z.object({ workspace_id: uuid, expected_version: counter }).strict().nullable().optional();
const saveBase = { schema_version: z.literal('feedback-workspace-save.v1'), operation_id: uuid, expected_version: counter,
  pending_reference_input: boundedText(2048).optional(), pending_reference_reason: boundedText(200).optional(),
  pending_reference_error: boundedText(1000).optional() };
const workspaceCommand = z.union([
  z.object({ action: z.literal('create'), body: z.object({ schema_version: z.literal('feedback-workspace-create.v1'),
    operation_id: uuid, feedback_id: uuid.nullable().optional(), expected_feedback_version: zeroCounter.optional(),
    entry_player_id: textId.nullable().optional() }).strict() }).strict(),
  z.object({ action: z.literal('save'), workspaceId: uuid, body: z.union([
    z.object({ ...saveBase, mode: z.literal('content'), content }).strict(),
    z.object({ ...saveBase, mode: z.literal('prose'), message: prose('message').optional(), next_step: prose('next_step').optional() }).strict(),
  ]) }).strict(),
  z.object({ action: z.literal('discard'), workspaceId: uuid, body: z.object({
    schema_version: z.literal('feedback-workspace-discard.v1'), operation_id: uuid, expected_version: counter }).strict() }).strict(),
]);
const draftBody = { schema_version: z.literal('feedback.v1'), operation_id: uuid, content, workspace };
const draftCommand = z.union([
  z.object({ action: z.literal('create'), body: z.object({ ...draftBody, expected_version: z.literal(0) }).strict() }).strict(),
  z.object({ action: z.literal('save'), feedbackId: uuid, body: z.object({ ...draftBody, expected_version: counter }).strict() }).strict(),
  z.object({ action: z.literal('discard'), feedbackId: uuid, body: z.object({ schema_version: z.literal('feedback-version.v1'),
    operation_id: uuid, expected_version: counter }).strict() }).strict(),
]);
const publishBody = { schema_version: z.literal('feedback-publish.v1'), operation_id: uuid, review_ticket: uuid, content, workspace };
const publicationCommand = z.union([
  z.object({ action: z.literal('new'), body: z.object({ ...publishBody, expected_version: z.literal(0) }).strict() }).strict(),
  z.object({ action: z.literal('existing'), feedbackId: uuid, body: z.object({ ...publishBody, expected_version: counter }).strict() }).strict(),
]);
const pendingSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('workspace'), command: workspaceCommand }).strict(),
  z.object({ kind: z.literal('draft'), command: draftCommand }).strict(),
  z.object({ kind: z.literal('publication'), command: publicationCommand }).strict(),
]);
export type FeedbackAuthoringPending =
  | { kind: 'workspace'; command: FeedbackWorkspaceCommand }
  | { kind: 'draft'; command: FeedbackDraftCommand }
  | { kind: 'publication'; command: FeedbackPublicationIntent };
const recordSchema = z.object({ schema_version: z.literal('feedback-continuation.v1'), entry_id: uuid,
  workspace_id: uuid.nullable(), feedback_id: uuid.nullable(), attempt: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER), pending: pendingSchema.nullable() }).strict();
export type FeedbackAuthoringContinuation = {
  schema_version: 'feedback-continuation.v1'; entry_id: string;
  workspace_id: string | null; feedback_id: string | null; attempt: number; pending: FeedbackAuthoringPending | null;
};
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const unavailable = () => new DOMException('Authoring context changed', 'AbortError');
const storageFailure = () => new Error('feedback_continuation_storage_unavailable');

/** Closed structural decoder only. Server admission remains the content policy.
 * No coercion/defaulting/trimming: replay keeps the exact captured wire intent. */
function decode(raw: string, entryId: string): FeedbackAuthoringContinuation {
  if (raw.length > 262144) throw new Error('feedback_continuation_invalid');
  const value: unknown = JSON.parse(raw);
  if (!recordSchema.safeParse(value).success || (value as FeedbackAuthoringContinuation).entry_id !== entryId) {
    throw new Error('feedback_continuation_invalid');
  }
  return value as FeedbackAuthoringContinuation;
}

function key(context: DraftContext, entryId: string): string {
  const prefix = feedbackContinuationPrefix(context);
  if (!prefix) throw unavailable();
  if (!uuid.safeParse(entryId).success) throw new Error('feedback_continuation_invalid');
  return prefix + entryId.toLowerCase();
}

export function loadFeedbackContinuation(context: DraftContext, entryId: string): FeedbackAuthoringContinuation | null {
  const storageKey = key(context, entryId);
  let raw: string | null;
  try { raw = sessionStorage.getItem(storageKey); } catch { throw storageFailure(); }
  return raw === null ? null : decode(raw, entryId.toLowerCase());
}

/** Enumeration is restricted to this currently authorized actor/scope partition. */
export function listFeedbackContinuations(context: DraftContext): FeedbackAuthoringContinuation[] {
  const prefix = feedbackContinuationPrefix(context);
  if (!prefix) throw unavailable();
  const keys: string[] = [];
  try {
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const candidate = sessionStorage.key(i);
      if (candidate?.startsWith(prefix)) keys.push(candidate.slice(prefix.length));
    }
  } catch { throw storageFailure(); }
  return keys.sort().map(id => loadFeedbackContinuation(context,id)).filter((row): row is FeedbackAuthoringContinuation =>
    row !== null && (row.pending !== null || row.workspace_id !== null || row.feedback_id !== null));
}

function write(context: DraftContext, value: FeedbackAuthoringContinuation): void {
  const storageKey = key(context, value.entry_id), raw = JSON.stringify(value);
  decode(raw,value.entry_id);
  try {
    sessionStorage.setItem(storageKey,raw);
    if (sessionStorage.getItem(storageKey) !== raw) throw storageFailure();
  } catch { throw storageFailure(); }
}

function entry(context: DraftContext, entryId: string): FeedbackAuthoringContinuation {
  return loadFeedbackContinuation(context,entryId) ?? { schema_version:'feedback-continuation.v1',
    entry_id:entryId.toLowerCase(), workspace_id:null, feedback_id:null, attempt:0, pending:null };
}

/** A known identity is only a navigation hint; its existing GET reauthorizes it. */
export function rememberFeedbackWorkspace(context: DraftContext, entryId: string, workspaceId: string): void {
  if (!uuid.safeParse(workspaceId).success) throw new Error('feedback_continuation_invalid');
  const row = entry(context,entryId);
  // A concurrent hydration may repeat an already-kept hint while an original
  // command is pending. Validated storage is sufficient; preserve every byte.
  if (row.workspace_id === workspaceId) return;
  if (row.pending) throw new Error('feedback_continuation_requires_original_reconciliation');
  write(context,{ ...row, workspace_id:workspaceId });
}

function persist(context: DraftContext, entryId: string, pending: FeedbackAuthoringPending): { attempt: number; adopted: boolean } {
  const row = entry(context,entryId);
  if (row.pending && JSON.stringify(row.pending) !== JSON.stringify(pending)) {
    throw new Error('feedback_continuation_requires_original_reconciliation');
  }
  const attempt = row.attempt + 1;
  write(context,{ ...row, attempt, pending:clone(pending) });
  return { attempt, adopted: row.pending !== null };
}

function accepted(context: DraftContext, entryId: string, pending: FeedbackAuthoringPending,
  attempt: number | null, result: object): void {
  if (!isCurrentDraftContext(context)) return;
  // Both reading and clearing are best effort after a validated acknowledgement.
  // A later attempt owns the journal even if this older response arrives last.
  try {
    const row = loadFeedbackContinuation(context,entryId);
    if (!row || row.attempt !== attempt || JSON.stringify(row.pending) !== JSON.stringify(pending)) return;
    if (pending.kind !== 'publication' && pending.command.action === 'discard') {
      // Retire payload/hints but keep the high-water attempt: reusing this entry
      // must not let an old controller release a newer same-command attempt.
      write(context,{ ...row, pending:null, workspace_id:null, feedback_id:null });
      return;
    }
    const workspaceId = 'workspace_id' in result && typeof result.workspace_id === 'string' ? result.workspace_id : row.workspace_id;
    const feedbackId = 'feedback_id' in result && typeof result.feedback_id === 'string' ? result.feedback_id : row.feedback_id;
    write(context,{ ...row, pending:null, workspace_id:workspaceId, feedback_id:feedbackId });
  } catch { /* retain original journal; the validated acknowledgement is real */ }
}

function releaseRejected(context: DraftContext, entryId: string, pending: FeedbackAuthoringPending,
  attempt: number | null, status: string) {
  if (status !== 'rejected') throw new Error('feedback_continuation_requires_original_reconciliation');
  const row = loadFeedbackContinuation(context,entryId);
  if (!row || row.attempt !== attempt || JSON.stringify(row.pending) !== JSON.stringify(pending)) {
    throw new Error('feedback_continuation_requires_original_reconciliation');
  }
  write(context,{ ...row, pending:null });
}

function commandRecovery(context: DraftContext, entryId: string,
  pending: Extract<FeedbackAuthoringPending,{ kind:'workspace' | 'draft' }>, restoredUnknown: boolean) {
  // Validate before capture so caller mutation and malformed storage cannot
  // introduce a different target, ticket, operation or precision-lost Goal.
  const captured = clone(pending);
  if (!pendingSchema.safeParse(captured).success) throw new Error('feedback_continuation_invalid');
  const controller = captured.kind === 'workspace'
    ? captureFeedbackWorkspaceCommand(context,captured.command,{ restoredUnknown })
    : captureFeedbackDraftCommand(context,captured.command,{ restoredUnknown });
  let attempt: number | null = null;
  let inFlight: Promise<Awaited<ReturnType<typeof controller.send>>> | null = null;
  return {
    getState: () => isCurrentDraftContext(context) ? controller.getState() : { status:'unavailable' as const },
    getIntent: () => isCurrentDraftContext(context) ? clone(captured) : null,
    releaseRejected: () => releaseRejected(context,entryId,captured,attempt,controller.getState().status),
    send(signal?: AbortSignal) {
      if (!isCurrentDraftContext(context) || signal?.aborted) return Promise.reject(unavailable());
      if (inFlight) return inFlight;
      try {
        if (controller.getState().status !== 'accepted') {
          const persisted = persist(context,entryId,captured);
          attempt = persisted.attempt;
          if (persisted.adopted) controller.adoptUnresolved();
        }
      } catch (error) { return Promise.reject(error); }
      inFlight = controller.send(signal).then(result => {
        accepted(context,entryId,captured,attempt,result);
        return result;
      }).finally(() => { inFlight = null; });
      return inFlight;
    },
  };
}

export function continueFeedbackWorkspace(context: DraftContext, entryId: string, command: FeedbackWorkspaceCommand) {
  return commandRecovery(context,entryId,{ kind:'workspace', command },false);
}
export function continueFeedbackDraft(context: DraftContext, entryId: string, command: FeedbackDraftCommand) {
  return commandRecovery(context,entryId,{ kind:'draft', command },false);
}

function reviewCandidate(command: FeedbackPublicationIntent): FeedbackReviewIntent {
  const { operation_id: _operation, review_ticket: _ticket, ...body } = command.body;
  return command.action === 'new' ? { action:'new', body:{ ...body, expected_version:0, schema_version:'feedback-review.v1' } }
    : { action:'existing', feedbackId:command.feedbackId, body:{ ...body, schema_version:'feedback-review.v1' } };
}

export function continueFeedbackPublication(context: DraftContext, entryId: string, candidate: FeedbackReviewIntent,
  restoredPublication?: FeedbackPublicationIntent) {
  if (restoredPublication && !publicationCommand.safeParse(restoredPublication).success) throw new Error('feedback_continuation_invalid');
  let attempt: number | null = null;
  const controller = captureFeedbackPublication(context,restoredPublication ? reviewCandidate(restoredPublication) : candidate,{
    restoredPublication, beforePublish: command => {
      const persisted = persist(context,entryId,{ kind:'publication', command });
      attempt = persisted.attempt;
      if (persisted.adopted) controller.adoptUnresolved();
    },
  });
  return {
    getState: () => isCurrentDraftContext(context) ? controller.getState() : { status:'unavailable' as const },
    getCandidate: () => isCurrentDraftContext(context) ? controller.getCandidate() : null,
    getPublicationIntent: () => isCurrentDraftContext(context) ? controller.getPublicationIntent() : null,
    releaseRejected() {
      const command = controller.getPublicationIntent();
      if (command) releaseRejected(context,entryId,{ kind:'publication', command },attempt,controller.getState().status);
    },
    review(signal?: AbortSignal) {
      if (!isCurrentDraftContext(context) || signal?.aborted) return Promise.reject(unavailable());
      if (entry(context,entryId).pending) return Promise.reject(new Error('feedback_continuation_requires_original_reconciliation'));
      return controller.review(signal);
    },
    async publish(operationId: string, signal?: AbortSignal): Promise<FeedbackPublicationResult> {
      if (!isCurrentDraftContext(context) || signal?.aborted) throw unavailable();
      const result = await controller.publish(operationId,signal);
      const command = controller.getPublicationIntent();
      if (command) accepted(context,entryId,{ kind:'publication', command },attempt,result);
      return result;
    },
  };
}

export function restoreFeedbackContinuation(context: DraftContext, entryId: string) {
  const pending = loadFeedbackContinuation(context,entryId)?.pending;
  if (!pending) return null;
  return pending.kind === 'publication'
    ? { kind:'publication' as const, controller:continueFeedbackPublication(context,entryId,reviewCandidate(pending.command),pending.command) }
    : { kind:pending.kind, controller:commandRecovery(context,entryId,pending,true) };
}
